import {useEffect} from 'react';
import {useNavigate} from 'react-router';
import clsx from 'clsx';
import {diApiEu} from '@di-shared/api/di';
import type {
  ApiResponseLatestScoreResponse,
  ApiResponseOdometerResponse,
  ApiResponseTermsConsentStatus,
} from '@di-shared/api/di/__generated_eu';
import {ERROR} from '@di-shared/const';
import {emitEventLog} from '@di-shared/features/analytics';
import Header from '@di-shared/features/common/Header';
import HeaderBackButton from '@di-shared/features/common/HeaderBackButton';
import ImageSelector from '@di-shared/features/common/ImageSelector';
import VariableDonutChart from '@di-shared/features/common/VariableDonutChart';
import useApiError from '@di-shared/features/common/useApiError';
import useCcWebviewUtilsHelper from '@di-shared/features/common/useCcWebviewUtilsHelper';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {useEnvironmentStore} from '@di-shared/features/environment/environmentStore';
import ConsentBannerEu from '@di-shared/features/home/eu/ConsentBanner.eu';
import {SafeDrivingSectionEuProps} from '@di-shared/features/home/eu/driving/SafeDrivingSection.eu';
import useAvailableComponentEu from '@di-shared/features/home/eu/useAvailableComponent.eu';
import LinkTo from '@di-shared/features/navigate/LinkTo';
import SafeDrivingSummaryGraphEu from '@di-shared/features/safe-driving/eu/SafeDrivingSummaryGraph.eu';
import {ROUTE_PATHS} from '@di-shared/routes';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import {ccWebviewUtils} from '@shared/cci/features/webviewUtils';
import {differenceInDay, parseDateWithFormat, subDate} from '@shared/dateUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import usePendingHandler from '@shared/usePendingHandler';
import {WebviewUtils} from '@shared/webviewUtils';
import {useQueryClient, useSuspenseQueries} from '@tanstack/react-query';

const MAX_AGREE_DAYS = 187;

export interface SafeDrivingContentsEuProps {
  vin: string;
}

function SafeDrivingContentsEu({vin}: SafeDrivingContentsEuProps) {
  const {t} = useInferableTranslation();
  const {resolveError} = useApiError();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const {openAlert, backOrClose} = useCcWebviewUtilsHelper();
  const {isPrimaryUserYn} = useAvailableComponentEu({vin});
  const today = new Date();
  const language = useEnvironmentStore((state) => state.lang);
  const [{data: ubiData}, {data: odometerData}, {data: termsData}] = useSuspenseQueries({
    queries: [
      {
        queryKey: [getQueryKey(diApiEu.safeDriving.latest), vin],
        queryFn: () => diApiEu.safeDriving.latest({vin}),
        select: ({data}: ApiResponseLatestScoreResponse) => {
          return {
            ...data,
            latestScoreInfo: {
              ...data.latestScoreInfo,
              lastUpdatedDate: parseDateWithFormat(data.latestScoreInfo.lastUpdatedDate),
            },
          };
        },
      },
      {
        queryKey: [getQueryKey(diApiEu.drivingHistory.retrieveOdometer), vin],
        queryFn: () => diApiEu.drivingHistory.retrieveOdometer({vin}),
        select: ({data}: ApiResponseOdometerResponse) => data,
      },
      {
        queryKey: [getQueryKey(diApiEu.vehicle.getConsentedSelectiveTermsCode), vin, 'SAFETY_SCORE'],
        queryFn: () =>
          diApiEu.vehicle.getConsentedSelectiveTermsCode({
            vin,
            termscode: 'SAFETY_SCORE',
          }),
        select: ({data}: ApiResponseTermsConsentStatus) => {
          return {
            ...data,
            consentDate: parseDateWithFormat(data.consentDate),
          };
        },
      },
    ],
  });

  async function openNoDataAlert() {
    await openAlert({
      title: t('msg_dss_LN_there_is_no_driving_history'),
      message: t('msg_dss_LN_no_driving_history_explain'),
      okText: t('ctn_knowing_data_05'),
    });
    backOrClose();
  }

  const [withdrawAgree] = usePendingHandler(
    async () => {
      await diApiEu.vehicle.consentSelectiveTermsCode({vin, termscode: 'SAFETY_SCORE', agree: false});
    },
    async (error) => {
      await WebviewUtils.openCustomAlert({
        title: t('imsi_sdc_error'),
        message: '',
        buttons: [{text: t('ctn_knowing_data_05'), style: 'PRIMARY', actionType: 'DEFAULT'}],
      });
      // TODO resolveError에 alert 등록해야하는지?
      await resolveError(error);
    }
  );

  async function openWithdrawAlert() {
    const selectedButton = await WebviewUtils.openCustomAlert({
      title: t('msg_dss_LN_disable_driving_safety_score_question'),
      message: t('msg_dss_LN_disable_driving_safety_score_question_explain'),
      buttons: [
        {text: t('ctn_dss_LN_cancel'), style: 'BASIC', actionType: 'CANCEL'},
        {text: t('ctn_dss_LN_disable_service'), style: 'PRIMARY', actionType: 'DEFAULT'},
        {text: t('msg_privacy_notice'), style: 'UNDERLINE', actionType: 'DEFAULT'},
      ],
    });
    if (selectedButton === 1) {
      await withdrawAgree();
      await queryClient.invalidateQueries();
      navigate(ROUTE_PATHS.HOME.getFullPath(vin));
      emitEventLog({
        event: 'driving_insight_interaction',
        form_step: 'score disable complete',
        click_text: t('ctn_dss_LN_disable_service'),
        click_location: 'body',
      });
    }
    if (selectedButton === 2) {
      WebviewUtils.openNewWebView({
        url: `${import.meta.env.VITE_NOTICE_EU_URL}?lang=${language}`,
        isModal: true,
        isNavigationBar: false,
        isToolBar: true,
      });
    }
  }

  useEffect(() => {
    if (ubiData.latestScoreInfo.drivingSafetyScore === 0 && odometerData.value === 0) {
      openNoDataAlert();
      return;
    }
  }, []);

  return (
    <>
      <Header left={<HeaderBackButton />}>
        <h1 className="headline_xxsmall nav_center_title">{t('ctn_dss_LN_driving_safety_score')}</h1>
        <div className="nav_box">
          <button
            type="button"
            className="btn btn_ic"
            aria-label={t('ctn_dss_open_tooltip')}
            onClick={async () => {
              await ccWebviewUtils.openBottomSheetWebViewUrl<boolean>(ROUTE_PATHS.BOTTOM_SHEET_SAFE_DRIVING_GUIDE.getFullPath(), {
                fromDate:
                  ubiData.latestScoreInfo.lastUpdatedDate && termsData.consentDate
                    ? differenceInDay(ubiData.latestScoreInfo.lastUpdatedDate, termsData.consentDate) > MAX_AGREE_DAYS - 1
                      ? CldrDatetimeFormatter.format(subDate(ubiData.latestScoreInfo.lastUpdatedDate, {days: MAX_AGREE_DAYS - 1}), 'yMMMd')
                      : CldrDatetimeFormatter.format(termsData.consentDate, 'yMMMd')
                    : '',
                toDate: ubiData.latestScoreInfo.lastUpdatedDate
                  ? CldrDatetimeFormatter.format(ubiData.latestScoreInfo.lastUpdatedDate, 'yMMMd')
                  : '',
              });
            }}
          >
            <span
              className="ic ic_information_20px"
              aria-hidden="true"
            ></span>
          </button>
        </div>
      </Header>

      <main
        id="content"
        className="content page_safe_driving"
      >
        <ConsentBannerEu />

        <section className="section section_safe_driving_report">
          <div className="section_heading">
            <h2
              className="section_heading_title"
              style={{whiteSpace: 'pre-wrap'}}
            >
              {t('ctn_dss_LN_based_on_history')}
              <span className="badge badge_basic_container badge_20px">
                {termsData.consentDate && ubiData.latestScoreInfo.lastUpdatedDate
                  ? differenceInDay(ubiData.latestScoreInfo.lastUpdatedDate, termsData.consentDate) >= 0
                    ? `D+${Math.min(differenceInDay(ubiData.latestScoreInfo.lastUpdatedDate, termsData.consentDate) + 1, MAX_AGREE_DAYS)}`
                    : `D-${differenceInDay(termsData.consentDate, ubiData.latestScoreInfo.lastUpdatedDate) + 1}`
                  : ERROR.DASH}
              </span>
            </h2>
          </div>
          <SafeDrivingSummaryGraphEu
            score={ubiData.latestScoreInfo.drivingSafetyScore}
            scoreDiff={ubiData.latestScoreInfo.previousScoreDelta}
          />
          <div className="trip_summary">
            <div className="trip_summary_list type_center">
              <div className="trip_summary_item">
                <span className="trip_summary_title">{t('ctn_dss_LN_all_cars')}</span>
                <div className="trip_summary_info">
                  <span className="trip_summary_value">
                    {ubiData.latestScoreInfo.drivingSafetyScore === 0
                      ? t('ctn_home_dss_top_percent_not')
                      : t('ctn_dss_LN_top_n_percent_$percent', {percent: ubiData.latestScoreInfo.rank})}
                  </span>
                </div>
              </div>
              <div className="divider divider_vertical"></div>
              <div className="trip_summary_item">
                <span className="trip_summary_title">
                  {ubiData.latestScoreInfo.drivingSafetyScore === 100
                    ? t('ctn_dss_LN_top_score')
                    : t('ctn_dss_LN_until_n_percent_$percent', {percent: ubiData.latestScoreInfo.nextRank})}
                </span>
                <div className="trip_summary_info">
                  <span className="trip_summary_value">
                    {ubiData.latestScoreInfo.drivingSafetyScore === 100
                      ? t('ctn_dss_LN_great_work')
                      : ubiData.latestScoreInfo.drivingSafetyScore === 0
                        ? t('ctn_dss_LN_just_drive')
                        : t('ctn_dss_LN_remaining_n_points_$score', {score: ubiData.latestScoreInfo.remainingPointToNextRank})}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <span className="section_container_caption">
            {t('cts_dss_LN_last_updated')}&nbsp;
            {ubiData.latestScoreInfo.lastUpdatedDate
              ? CldrDatetimeFormatter.format(ubiData.latestScoreInfo.lastUpdatedDate, 'yMMMaHM')
              : termsData.consentDate
                ? CldrDatetimeFormatter.format(termsData.consentDate, 'yMMMaHM')
                : CldrDatetimeFormatter.format(today, 'yMMMaHM')}
          </span>
          {!ubiData.sufficientTripDataAvailable && (
            <div className="driving_banner type_square">
              <div className="driving_banner_box">
                <span className="driving_banner_txtbox">
                  <strong className="driving_banner_title body_medium_b">{t('ctn_dss_LN_additional_driving_data_required')}</strong>
                  <span className="driving_banner_desc body_small_r">{t('msg_dss_LN_additional_driving_data_required_explain')}</span>
                </span>
                <span className="driving_banner_imgbox type_small">
                  <ImageSelector
                    className="driving_banner_img"
                    width="48"
                    height="80"
                    alt="driving_banner_img"
                    kiaLightSrc={() => import('@di-shared/assets/img/banner/banner_eco_tip.png')}
                    kiaDarkSrc={() => import('@di-shared/assets/img/banner/banner_eco_tip.png')}
                    hyundaiLightSrc={() => import('@di-shared/assets/img/banner/banner_eco_tip.png')}
                    hyundaiDarkSrc={() => import('@di-shared/assets/img/banner/banner_eco_tip.png')}
                    genesisLightSrc={() => import('@di-shared/assets/img/banner/banner_eco_tip.png')}
                    genesisDarkSrc={() => import('@di-shared/assets/img/banner/banner_eco_tip.png')}
                  />
                </span>
              </div>
            </div>
          )}

          <div className="section_container">
            <span className="blind">Driving safety score graph info</span>
            <SafeDrivingSubScoreBox
              title={t('ctn_dss_LN_estimated_annual_driving_distance')}
              subScore={ubiData.latestScoreInfo.subScoreDetailResponse.estimatedAnnualMileage}
            />
            <SafeDrivingSubScoreBox
              title={t('ctn_dss_LN_hard_braking')}
              subScore={ubiData.latestScoreInfo.subScoreDetailResponse.hardBraking}
            />
            <SafeDrivingSubScoreBox
              title={t('ctn_dss_LN_average_speed')}
              subScore={ubiData.latestScoreInfo.subScoreDetailResponse.averageTripSpeed}
            />
            <SafeDrivingSubScoreBox
              title={t('ctn_dss_LN_maximum_speed')}
              subScore={ubiData.latestScoreInfo.subScoreDetailResponse.maximumHighSpeed}
            />
            <SafeDrivingSubScoreBox
              title={t('ctn_dss_LN_driving_hours')}
              subScore={ubiData.latestScoreInfo.subScoreDetailResponse.drivingTimeOfDay}
            />
          </div>
          <div className="driving_banner type_square">
            <LinkTo
              to={ROUTE_PATHS.SAFE_DRIVING_REPORT.getFullPath(vin)}
              className="driving_banner_box"
            >
              <span className="driving_banner_txtbox">
                <strong className="driving_banner_title body_large_b">{t('ctn_dss_LN_driving_safety_report')}</strong>
                <span className="driving_banner_desc body_small_r">{t('msg_dss_LN_driving_safety_report_explain')}</span>
              </span>
              <span className="driving_banner_imgbox ">
                <ImageSelector
                  className="driving_banner_img"
                  width="80"
                  height="80"
                  alt="driving_banner_img"
                  kiaLightSrc={() => import('@di-shared/assets/img/banner/banner_safe_score.png')}
                  kiaDarkSrc={() => import('@di-shared/assets/img/banner/banner_safe_score.png')}
                  hyundaiLightSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_safe_score.png')}
                  hyundaiDarkSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_safe_score.png')}
                  genesisLightSrc={() => import('@di-shared/assets/img/banner/genesis/banner_safe_score.png')}
                  genesisDarkSrc={() => import('@di-shared/assets/img/banner/genesis/banner_safe_score.png')}
                />
              </span>
            </LinkTo>
          </div>
          {isPrimaryUserYn && (
            <button
              type="button"
              className="consent_withdraw_btn"
              onClick={() => {
                emitEventLog({
                  event: 'driving_insight_interaction',
                  form_step: 'score disable start',
                  click_text: t('ctn_dss_LN_disable_driving_safety_score'),
                  click_location: 'body',
                });
                openWithdrawAlert();
              }}
            >
              {t('ctn_dss_LN_disable_driving_safety_score')}
            </button>
          )}
        </section>
      </main>
    </>
  );
}

function SafeDrivingSubScoreBox({title, subScore}: {title: string; subScore: number}) {
  return (
    <div className="bar_graph_box">
      <strong className="title">{title}</strong>
      <div className="bar_graph type_single">
        <div
          className={clsx('bar_graph_item is-active', {
            type_low: subScore <= 3,
            type_warning: 4 <= subScore && subScore <= 5,
            type_caution: 6 <= subScore && subScore <= 7,
            type_good: 8 <= subScore && subScore <= 9,
            type_maximum: subScore === 10,
          })}
        >
          <span
            className="graph_bar"
            style={{width: `${subScore * 10}%`}}
          >
            <span className="speech_bubble">{subScore}</span>
          </span>
        </div>
      </div>
    </div>
  );
}

function SafeDrivingContentsErrorEu({vin}: SafeDrivingSectionEuProps) {
  const {t} = useInferableTranslation();

  return (
    <>
      <Header left={<HeaderBackButton />}>
        <h1 className="headline_xxsmall nav_center_title">{t('ctn_dss_LN_driving_safety_score')}</h1>
        <div className="nav_box"></div>
      </Header>
      <main
        id="content"
        className="content page_safe_driving"
      >
        <section className="section section_safe_driving_report">
          <div className="section_heading">
            <h2
              className="section_heading_title"
              style={{whiteSpace: 'pre-wrap'}}
            >
              {t('ctn_dss_LN_based_on_history')}
            </h2>
          </div>
          <div className="driving_score_graph_area">
            <VariableDonutChart
              color={''}
              ratio={0}
              width={190}
              strokeWidth={8}
              angle={45}
            />
            <div className="driving_mileage_graph_info">
              <span className="driving_mileage_graph_score">
                <span className="score_value">{ERROR.DASH}</span>
              </span>
              <span className="driving_mileage_graph_compare">
                <span className="compare_value type_plus">{ERROR.DASH}</span>
                <span className="compare_text">{t('ctn_home_dss_points')}</span>
              </span>
            </div>
          </div>
          <div className="trip_summary">
            <div className="trip_summary_list type_center type_error">
              <div className="trip_summary_item">
                <span className="trip_summary_title">{t('ctn_dss_LN_all_cars')}</span>
                <div className="trip_summary_info">
                  <span className="trip_summary_value">{ERROR.DASH}</span>
                </div>
              </div>
              <div className="divider divider_vertical"></div>
              <div className="trip_summary_item">
                <span className="trip_summary_title">{t('ctn_dss_LN_until_n_percent_$percent', {percent: ERROR.DASH})}</span>
                <div className="trip_summary_info">
                  <span className="trip_summary_value">{ERROR.DASH}</span>
                </div>
              </div>
            </div>
          </div>
          <span className="section_container_caption">
            {t('cts_dss_LN_last_updated')}
            {ERROR.DASH}
          </span>

          <div className="section_container">
            <span className="blind">Driving safety score graph info</span>
            <div className="bar_graph_box">
              <strong className="title">{t('ctn_dss_LN_estimated_annual_driving_distance')}</strong>
              <div className="bar_graph type_single">
                <div className="bar_graph_item is-active type_low">
                  <span
                    className="graph_bar"
                    style={{width: '0%'}}
                  ></span>
                </div>
              </div>
            </div>
            <div className="bar_graph_box">
              <strong className="title">{t('ctn_dss_LN_hard_braking')}</strong>
              <div className="bar_graph type_single">
                <div className="bar_graph_item is-active type_warning">
                  <span
                    className="graph_bar"
                    style={{width: '0%'}}
                  ></span>
                </div>
              </div>
            </div>
            <div className="bar_graph_box">
              <strong className="title">{t('ctn_dss_LN_average_speed')}</strong>
              <div className="bar_graph type_single">
                <div className="bar_graph_item is-active type_caution">
                  <span
                    className="graph_bar"
                    style={{width: '0%'}}
                  ></span>
                </div>
              </div>
            </div>
            <div className="bar_graph_box">
              <strong className="title">{t('ctn_dss_LN_maximum_speed')}</strong>
              <div className="bar_graph type_single">
                <div className="bar_graph_item is-active type_good">
                  <span
                    className="graph_bar"
                    style={{width: '0%'}}
                  ></span>
                </div>
              </div>
            </div>
            <div className="bar_graph_box">
              <strong className="title">{t('ctn_dss_LN_driving_hours')}</strong>
              <div className="bar_graph type_single">
                <div className="bar_graph_item is-active type_maximum">
                  <span
                    className="graph_bar"
                    style={{width: '0%'}}
                  ></span>
                </div>
              </div>
            </div>
          </div>

          <div className="driving_banner type_square">
            <LinkTo
              to={ROUTE_PATHS.SAFE_DRIVING_REPORT.getFullPath(vin)}
              className="driving_banner_box"
            >
              <span className="driving_banner_txtbox">
                <strong className="driving_banner_title body_large_b">{t('ctn_dss_LN_driving_safety_report')}</strong>
                <span className="driving_banner_desc body_small_r">{t('msg_dss_LN_driving_safety_report_explain')}</span>
              </span>
              <span className="driving_banner_imgbox ">
                <ImageSelector
                  className="driving_banner_img"
                  width="80"
                  height="80"
                  alt="driving_banner_img"
                  kiaLightSrc={() => import('@di-shared/assets/img/banner/banner_safe_score.png')}
                  kiaDarkSrc={() => import('@di-shared/assets/img/banner/banner_safe_score.png')}
                  hyundaiLightSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_safe_score.png')}
                  hyundaiDarkSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_safe_score.png')}
                  genesisLightSrc={() => import('@di-shared/assets/img/banner/genesis/banner_safe_score.png')}
                  genesisDarkSrc={() => import('@di-shared/assets/img/banner/genesis/banner_safe_score.png')}
                />
              </span>
            </LinkTo>
          </div>
        </section>
      </main>
    </>
  );
}

function SafeDrivingContentsLoadingEu() {
  const {t} = useInferableTranslation();

  return (
    <>
      <Header left={<HeaderBackButton />}>
        <h1 className="headline_xxsmall nav_center_title">{t('ctn_dss_LN_driving_safety_score')}</h1>
        <div className="nav_box"></div>
      </Header>
      <main
        id="content"
        className="content page_safe_driving"
      >
        <section className="section section_safe_driving_report">
          <div className="section_heading skeleton">
            <h2
              className="section_heading_title"
              style={{whiteSpace: 'pre-wrap'}}
            >
              {t('ctn_dss_LN_based_on_history')}
              <span className="badge badge_basic_container badge_20px">D+187</span>
            </h2>
          </div>
          <div className="driving_score_graph_area skeleton">
            <VariableDonutChart
              color={''}
              ratio={0}
              width={0}
              strokeWidth={8}
              angle={45}
            />
          </div>

          <div className="trip_summary skeleton">
            {/*// <!-- [D] 에러 케이스일 경우 trip_summary_list에 type_error 클래스 추가 -->*/}
            <div className="trip_summary_list type_center">
              <div className="trip_summary_item">
                <span className="trip_summary_title">{t('ctn_dss_LN_all_cars')}</span>
                <div className="trip_summary_info">
                  <span className="trip_summary_value">{t('ctn_dss_LN_top_n_percent_$percent', {percent: '10000'})}</span>
                </div>
              </div>
              <div className="divider divider_vertical"></div>
              <div className="trip_summary_item">
                <span className="trip_summary_title">{t('ctn_dss_LN_until_n_percent_$percent', {percent: '10000'})}</span>
                <div className="trip_summary_info">
                  <span className="trip_summary_value">{t('ctn_dss_LN_remaining_n_points_$score', {score: '10000'})}</span>
                </div>
              </div>
            </div>
          </div>
          <span className="section_container_caption skeleton">
            {t('cts_dss_LN_last_updated')}
            {'21-02-2023 09:54'}
          </span>

          <div className="driving_banner type_square skeleton">
            <a
              href="#"
              className="driving_banner_box"
            >
              <span className="driving_banner_txtbox">
                <strong className="driving_banner_title body_medium_b">{t('ctn_dss_LN_additional_driving_data_required')}</strong>
                <span className="driving_banner_desc body_small_r">{t('msg_dss_LN_additional_driving_data_required_explain')}</span>
              </span>
              <span className="driving_banner_imgbox type_small">
                <ImageSelector
                  className="driving_banner_img"
                  width="48"
                  height="80"
                  alt="driving_banner_img"
                  kiaLightSrc={() => import('@di-shared/assets/img/banner/banner_driving_safety.png')}
                  kiaDarkSrc={() => import('@di-shared/assets/img/banner/banner_driving_safety.png')}
                  hyundaiLightSrc={() => import('@di-shared/assets/img/banner/banner_driving_safety.png')}
                  hyundaiDarkSrc={() => import('@di-shared/assets/img/banner/banner_driving_safety.png')}
                  genesisLightSrc={() => import('@di-shared/assets/img/banner/banner_driving_safety.png')}
                  genesisDarkSrc={() => import('@di-shared/assets/img/banner/banner_driving_safety.png')}
                />
              </span>
            </a>
          </div>

          <div className="section_container skeleton">
            <span className="blind">Driving safety score graph info</span>
            <div className="bar_graph_box">
              <strong className="title">{t('ctn_dss_LN_estimated_annual_driving_distance')}</strong>
              <div className="bar_graph type_single">
                {/*// <!-- [D] 활성화 bar_graph_item에 is-active 클래스 추가 -->*/}
                <div className="bar_graph_item is-active type_low">
                  {/*//         <!-- [D] graph bar상태에 따라 클래스 적용*/}
                  {/*//   red: type_low*/}
                  {/*//   light red: type_warning*/}
                  {/*//   yellow: type_caution*/}
                  {/*//   green: type_good*/}
                  {/*//   light green: type_maximum*/}
                  {/*// -->*/}
                  <span
                    className="graph_bar"
                    style={{width: '30%'}}
                  >
                    <span className="speech_bubble">3</span>
                  </span>
                </div>
              </div>
            </div>
            <div className="bar_graph_box">
              <strong className="title">{t('ctn_dss_LN_hard_braking')}</strong>
              <div className="bar_graph type_single">
                <div className="bar_graph_item is-active type_warning">
                  <span
                    className="graph_bar"
                    style={{width: '40%'}}
                  >
                    <span className="speech_bubble">4</span>
                  </span>
                </div>
              </div>
            </div>
            <div className="bar_graph_box">
              <strong className="title">{t('ctn_dss_LN_average_speed')}</strong>
              <div className="bar_graph type_single">
                <div className="bar_graph_item is-active type_caution">
                  <span
                    className="graph_bar"
                    style={{width: '70%'}}
                  >
                    <span className="speech_bubble">7</span>
                  </span>
                </div>
              </div>
            </div>
            <div className="bar_graph_box">
              <strong className="title">{t('ctn_dss_LN_maximum_speed')}</strong>
              <div className="bar_graph type_single">
                <div className="bar_graph_item is-active type_good">
                  <span
                    className="graph_bar"
                    style={{width: '90%'}}
                  >
                    <span className="speech_bubble">9</span>
                  </span>
                </div>
              </div>
            </div>
            <div className="bar_graph_box">
              <strong className="title">{t('ctn_dss_LN_driving_hours')}</strong>
              <div className="bar_graph type_single">
                <div className="bar_graph_item is-active type_maximum">
                  <span
                    className="graph_bar"
                    style={{width: '100%'}}
                  >
                    <span className="speech_bubble">10</span>
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="driving_banner type_square skeleton">
            <a className="driving_banner_box">
              <span className="driving_banner_txtbox">
                <strong className="driving_banner_title body_large_b">{t('ctn_dss_LN_driving_safety_report')}</strong>
                <span className="driving_banner_desc body_small_r">{t('msg_dss_LN_driving_safety_report_explain')}</span>
              </span>
              <span className="driving_banner_imgbox ">
                <ImageSelector
                  className="driving_banner_img"
                  width="80"
                  height="80"
                  alt="driving_banner_img"
                  kiaLightSrc={() => import('@di-shared/assets/img/banner/banner_safe_score.png')}
                  kiaDarkSrc={() => import('@di-shared/assets/img/banner/banner_safe_score.png')}
                  hyundaiLightSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_safe_score.png')}
                  hyundaiDarkSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_safe_score.png')}
                  genesisLightSrc={() => import('@di-shared/assets/img/banner/genesis/banner_safe_score.png')}
                  genesisDarkSrc={() => import('@di-shared/assets/img/banner/genesis/banner_safe_score.png')}
                />
              </span>
            </a>
          </div>
        </section>
      </main>
    </>
  );
}

// FALLBACK(LOADING, ERROR) UI
SafeDrivingContentsEu.Error = ({vin}: {vin: string}) => <SafeDrivingContentsErrorEu vin={vin} />;
SafeDrivingContentsEu.Loading = () => <SafeDrivingContentsLoadingEu />;

export default SafeDrivingContentsEu;
